import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Star, MessageSquare, Lightbulb, Clock, Headphones, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';

// StarRating component moved outside main component
const StarRating = ({ value, onChange, label, icon: Icon }) => {
  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-slate-500" />
        <span className="text-sm font-medium text-slate-700">{label}</span>
      </div>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            className="transition-transform hover:scale-110 focus:outline-none"
          >
            <Star
              className={`w-8 h-8 transition-colors ${
                star <= value
                  ? 'text-amber-400 fill-amber-400'
                  : 'text-slate-300 hover:text-amber-200'
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

const SatisfactionSurveyPopup = ({ onClose }) => {
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [thankYouMessage, setThankYouMessage] = useState('');
  
  // Scores
  const [listeningScore, setListeningScore] = useState(0);
  const [creativityScore, setCreativityScore] = useState(0);
  const [adviceScore, setAdviceScore] = useState(0);
  const [deadlineBudgetScore, setDeadlineBudgetScore] = useState(0);
  const [comment, setComment] = useState('');

  const fetchPendingSurvey = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/satisfaction-surveys/pending');
      if (response.data) {
        setSurvey(response.data);
      }
    } catch (error) {
      console.error('Error fetching survey:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingSurvey();
  }, [fetchPendingSurvey]);

  const handleSubmit = async () => {
    if (listeningScore === 0 || creativityScore === 0 || adviceScore === 0 || deadlineBudgetScore === 0) {
      toast.error('Veuillez noter tous les critères');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post(`/satisfaction-surveys/${survey.id}/submit`, {
        listening_score: listeningScore,
        creativity_score: creativityScore,
        advice_score: adviceScore,
        deadline_budget_score: deadlineBudgetScore,
        comment: comment || null
      });
      
      setThankYouMessage(response.data.message);
      setShowThankYou(true);
      
      // Close after showing thank you message
      setTimeout(() => {
        onClose?.();
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = async () => {
    try {
      await api.post(`/satisfaction-surveys/${survey.id}/skip`);
      onClose?.();
    } catch (error) {
      console.error('Error skipping survey:', error);
      onClose?.();
    }
  };

  if (loading) {
    return null;
  }

  if (!survey) {
    return null;
  }

  if (showThankYou) {
    return (
      <Dialog open={true} onOpenChange={() => {}}>
        <DialogContent className="max-w-md">
          <div className="text-center py-8">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
              <Star className="w-10 h-10 text-white fill-white" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Merci !</h2>
            <p className="text-slate-600">{thankYouMessage}</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const totalScore = listeningScore + creativityScore + adviceScore + deadlineBudgetScore;
  const canSubmit = listeningScore > 0 && creativityScore > 0 && adviceScore > 0 && deadlineBudgetScore > 0;

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="satisfaction-popup">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="p-2 rounded-full bg-gradient-to-br from-amber-400 to-amber-600">
              <Star className="w-5 h-5 text-white" />
            </div>
            <span>Votre avis compte !</span>
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          <p className="text-sm text-slate-600 mb-6">
            {survey.project_name ? (
              <>Comment s&apos;est passée votre expérience avec le projet <strong>{survey.project_name}</strong> ?</>
            ) : survey.task_name ? (
              <>Comment s&apos;est passée votre expérience avec la tâche <strong>{survey.task_name}</strong> ?</>
            ) : (
              <>Comment s&apos;est passée votre expérience ?</>
            )}
          </p>

          <StarRating
            value={listeningScore}
            onChange={setListeningScore}
            label="L'écoute"
            icon={Headphones}
          />

          <StarRating
            value={creativityScore}
            onChange={setCreativityScore}
            label="La créativité"
            icon={Lightbulb}
          />

          <StarRating
            value={adviceScore}
            onChange={setAdviceScore}
            label="Le conseil"
            icon={MessageSquare}
          />

          <StarRating
            value={deadlineBudgetScore}
            onChange={setDeadlineBudgetScore}
            label="Le respect du délai et du budget"
            icon={Clock}
          />

          {canSubmit && (
            <div className="mb-4 p-3 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border border-amber-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-amber-800">Votre note globale</span>
                <span className="text-2xl font-bold text-amber-600">{totalScore}/20</span>
              </div>
            </div>
          )}

          <div className="mb-4">
            <label className="text-sm font-medium text-slate-700 mb-2 block">
              Un commentaire ? (optionnel)
            </label>
            <Textarea
              placeholder="Partagez votre expérience..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="ghost"
            onClick={handleSkip}
            className="text-slate-500"
            disabled={submitting}
          >
            <X className="w-4 h-4 mr-2" />
            Pas maintenant
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Star className="w-4 h-4 mr-2" />
            )}
            Envoyer mon avis
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SatisfactionSurveyPopup;
